import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw { status: res.status, detail: data.detail || "Error" };
  return data;
}

function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  return digits;
}

/** Returns minutes remaining until expiresAt (ISO string). Negative if past. */
function minutesRemaining(expiresAt) {
  return Math.floor((new Date(expiresAt) - Date.now()) / 60000);
}

function TabOnLogo({ primaryColor }) {
  return (
    <div style={{ fontFamily: "inherit" }}>
      <span style={{ color: primaryColor || "#9D00FF", fontWeight: 900, fontSize: 22, letterSpacing: -1 }}>Tab</span>
      <span style={{ color: "#fff", fontWeight: 900, fontSize: 22, letterSpacing: -1 }}>On</span>
    </div>
  );
}

function Spinner({ primaryColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: `3px solid ${primaryColor ? primaryColor + "33" : "rgba(157,0,255,0.2)"}`,
        borderTopColor: primaryColor || "#9D00FF",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function Split() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | error | expired | fully_claimed | active
  const [invite, setInvite] = useState(null);

  // Detect ?claim=1 on mount to route friends directly to claim view
  const startOnClaim = new URLSearchParams(window.location.search).get("claim") === "1";
  const [view, setView] = useState(startOnClaim ? "claim" : "owner");

  // Expiry countdown (minutes remaining, updated every 30s)
  const [minsLeft, setMinsLeft] = useState(null);

  // Claim state
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [showName, setShowName] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState("");
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => {
    apiFetch(`/api/public/split/${token}`)
      .then((data) => {
        if (data.status === "expired") { setStatus("expired"); return; }
        if (data.status === "fully_claimed") { setStatus("fully_claimed"); return; }
        setInvite(data);
        setMinsLeft(minutesRemaining(data.expires_at));
        setStatus("active");
      })
      .catch((err) => {
        const msg = (err?.detail || "").toLowerCase();
        if (err?.status === 404) { setStatus("error_invalid"); return; }
        if (msg.includes("expired")) { setStatus("expired"); return; }
        setStatus("error_unavailable");
      });
  }, [token]);

  // Refresh countdown every 30s
  useEffect(() => {
    if (!invite) return;
    const id = setInterval(() => setMinsLeft(minutesRemaining(invite.expires_at)), 30000);
    return () => clearInterval(id);
  }, [invite]);

  // Derive branding (defaults used before invite loads)
  const b = invite?.venue_branding || {};
  const primary    = b.primary_color    || "#9D00FF";
  const bg         = b.background_color || "#0A0A0A";
  const textColor  = b.text_color       || "#FFFFFF";
  const cardColor  = b.card_color       || "#1A1A1A";
  const accentText = b.accent_text_color|| "#A0A0A0";
  const logoUrl    = b.logo_url         || null;

  // Share URL: append ?claim=1 so friends land on claim view
  const shareUrl = `${window.location.origin}/split/${token}?claim=1`;
  const waText = invite
    ? `I'm sharing my ${invite.venue_name} loyalty points with you! Claim your ${invite.per_person_points} pts here → ${shareUrl}`
    : "";

  const handleClaim = async () => {
    setClaimError("");
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) { setClaimError("Enter a valid 10-digit mobile number"); return; }
    setClaimLoading(true);
    try {
      const data = await apiFetch(`/api/public/split/${token}/claim`, {
        method: "POST",
        body: JSON.stringify({ phone: normalized, name: showName ? name.trim() : undefined }),
      });
      setClaimResult(data);
      setView("success");
    } catch (err) {
      const msg = err?.detail || "";
      if (msg.includes("own link")) {
        setView("owner");
        return;
      }
      if (msg.includes("Name required")) {
        setShowName(true);
        setClaimError("Enter your name to continue");
      } else if (msg.includes("expired")) {
        setClaimError("This link has expired.");
      } else if (msg.includes("Already claimed")) {
        setClaimError("You've already claimed your share.");
      } else if (msg.includes("All shares")) {
        setClaimError("All shares have been claimed.");
      } else {
        setClaimError("Unable to claim. Please try again.");
      }
    } finally {
      setClaimLoading(false);
    }
  };

  // Expiry label shown on claim view
  const expiryNode = invite ? (() => {
    if (minsLeft === null) return null;
    if (minsLeft < 60) {
      return (
        <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 4 }}>
          Hurry! Expires in {Math.max(0, minsLeft)} minute{minsLeft !== 1 ? "s" : ""}
        </div>
      );
    }
    const formatted = new Date(invite.expires_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    return (
      <div style={{ fontSize: 12, color: accentText, marginTop: 4 }}>
        Claim before {formatted}
      </div>
    );
  })() : null;

  const container = {
    minHeight: "100dvh",
    background: bg,
    color: textColor,
    fontFamily: "'Inter', system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 20px",
  };

  const card = {
    width: "100%",
    maxWidth: 360,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 20,
  };

  const btn = {
    width: "100%",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "14px 0",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background 0.15s",
  };

  const inputStyle = {
    border: `1px solid ${invite?.venue_branding?.text_color || "#000000"}40`,
    borderRadius: 8,
    padding: "12px 16px",
    fontSize: 16,
    width: "100%",
    background: "transparent",
    color: invite?.venue_branding?.text_color || "#000000",
    outline: "none",
    boxSizing: "border-box",
  };

  const poweredBy = (
    <div style={{ textAlign: "center", marginTop: 32 }}>
      <span style={{ color: "#888888", fontSize: 12 }}>Powered by{" "}</span>
      <span style={{ color: "#9D00FF", fontSize: 12, fontWeight: 600 }}>TabOn</span>
    </div>
  );

  const LogoArea = () => logoUrl ? (
    <img src={logoUrl} alt="venue logo" style={{ maxHeight: 60, maxWidth: 160, objectFit: "contain" }} />
  ) : (
    <TabOnLogo primaryColor={primary} />
  );

  // ── LOADING ──
  if (status === "loading") return (
    <div style={{ ...container, background: "#0A0A0A" }}>
      <Spinner primaryColor="#9D00FF" />
    </div>
  );

  // ── ERROR: invalid ──
  if (status === "error_invalid") return (
    <div style={container}>
      <div style={card}>
        <TabOnLogo primaryColor="#9D00FF" />
        <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>This link is invalid.</div>
        {poweredBy}
      </div>
    </div>
  );

  // ── ERROR: unavailable ──
  if (status === "error_unavailable") return (
    <div style={container}>
      <div style={card}>
        <TabOnLogo primaryColor="#9D00FF" />
        <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>This link is unavailable.</div>
        {poweredBy}
      </div>
    </div>
  );

  // ── EXPIRED ──
  if (status === "expired") return (
    <div style={container}>
      <div style={card}>
        <TabOnLogo primaryColor="#9D00FF" />
        <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>This link has expired.</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
          Loyalty points have been returned to the original customer.
        </div>
        {poweredBy}
      </div>
    </div>
  );

  // ── FULLY CLAIMED ──
  if (status === "fully_claimed") return (
    <div style={container}>
      <div style={card}>
        <TabOnLogo primaryColor="#9D00FF" />
        <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>All shares have been claimed.</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
          All points have been distributed.
        </div>
        {poweredBy}
      </div>
    </div>
  );

  // ── SUCCESS VIEW ──
  if (view === "success" && claimResult) return (
    <div style={container}>
      <div style={{ ...card, textAlign: "center" }}>
        <LogoArea />
        <div style={{ fontSize: 48 }}>✅</div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {claimResult.points_credited} points added to<br />your {claimResult.venue_name} account!
          </div>
          <div style={{ fontSize: 14, color: accentText, marginTop: 10 }}>
            Visit {claimResult.venue_name} to redeem your rewards.
          </div>
        </div>
        {poweredBy}
      </div>
    </div>
  );

  // ── CLAIM VIEW ──
  if (view === "claim") return (
    <div style={container}>
      <div style={card}>
        <LogoArea />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.3 }}>
            {invite.referrer_name} is sharing<br />
            <span style={{ color: primary }}>{invite.per_person_points} loyalty points</span> with you<br />
            from {invite.venue_name} 🎁
          </div>
        </div>

        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 13, color: accentText }}>Enter your mobile number</div>
          <input
            type="tel"
            style={inputStyle}
            placeholder="+91 __________"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {showName && (
            <input
              type="text"
              style={inputStyle}
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          {expiryNode}
          {claimError && (
            <div style={{ fontSize: 13, color: "#ff6b6b" }}>{claimError}</div>
          )}
          <button style={btn} onClick={handleClaim} disabled={claimLoading}>
            {claimLoading ? "Claiming…" : `Claim ${invite.per_person_points} Points`}
          </button>
          {!startOnClaim && (
            <button
              style={{ background: "none", border: "none", color: accentText, fontSize: 12, cursor: "pointer", marginTop: -4 }}
              onClick={() => setView("owner")}
            >
              ← Back
            </button>
          )}
        </div>
        {poweredBy}
      </div>
    </div>
  );

  // ── OWNER VIEW (default) ──
  return (
    <div style={container}>
      <div style={card}>
        <LogoArea />
        <div style={{ fontSize: 16, fontWeight: 700, color: accentText }}>{invite.venue_name}</div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.4 }}>
            Share your rewards with<br />the friends who dined<br />with you tonight 🎉
          </div>
          <div style={{ marginTop: 12, fontSize: 15, color: accentText }}>
            Each friend gets{" "}
            <span style={{ color: primary, fontWeight: 700 }}>{invite.per_person_points} pts</span>{" "}
            at {invite.venue_name}
          </div>
        </div>

        <a
          href={`whatsapp://send?text=${encodeURIComponent(waText)}`}
          style={{ ...btn, display: "block", textAlign: "center", textDecoration: "none", lineHeight: "1.2" }}
        >
          Share on WhatsApp
        </a>

        <div style={{ width: "100%", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, textAlign: "center" }}>
          <span style={{ color: accentText, fontSize: 13 }}>Not the host?{" "}</span>
          <button
            style={{ background: "none", border: "none", color: primary, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            onClick={() => setView("claim")}
          >
            Claim yours →
          </button>
        </div>

        <div style={{ fontSize: 12, color: accentText }}>
          Expires: {new Date(invite.expires_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </div>

        {poweredBy}
      </div>
    </div>
  );
}
